import React, { useState } from "react";
import Dictionary from "../../../utils/Dictionary";
import dictionaryData from "../../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };
  return (
    <div className="mb-20">
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <h2 id="Checking-that-EC20-is-expressed_e">
          Checking that EC20 is expressed{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>

        <h3 className="mb-5 mt-10" id="Western Blot">
          Western Blot
        </h3>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          In order to check that EC20 is <span>expressed</span> in our bacteria,
          we added a <span>His Tag</span> to our protein. We made a{" "}
          <span>Western blot</span> to be able to detect the His tag, fused to
          EC20. If our protein is expressed, we are able to detect it thanks to{" "}
          <span>chemiluminescence</span>. You can refer to{" "}
          <a href="#Figure1">Figure 1</a> for the scheme of the working
          principle of Western blot.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/ec20-western.png"
              alt="Experiment_EC20"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              Schematic of the working system of Western blot on EC20
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Western_Blot_protocol">
              <mark>
                <b>Western Blot protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/western-blot-wiki.pdf"
              width="800"
              height="500"
              aria-label="Western Blot protocol"
            ></object>
          </details>
                 
        </center>

        <h3 className="mb-5 mt-10" id="coomassie">
          {" "}
          Coomassie blue staining{" "}
        </h3>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Given the challenges encountered in detecting our His-tagged protein,
          our initial approach pivoted to identifying the protein directly via
          Coomassie blue staining. This widely-accepted method facilitates the
          visualization of proteins within <span>SDS-PAGE gels</span>. The
          presence of a band at the expected <span>molecular weight</span> would
          be indicative of EC20 expression.
        </p>

        <center>
          <details>
            <summary id="Coomassie">
              <mark>
                <b>Coomassie blue staining</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/coomassie-blue-staining.pdf"
              width="800"
              height="500"
              aria-label="Coomassie blue staining"
            ></object>
          </details>
                 
        </center>

        <h3 className="mb-5 mt-10" id="DTT and Urea">
          {" "}
          DTT and Urea added to the boiling step, followed by Western blot{" "}
        </h3>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Given the lack of success with previous experimental approaches, we
          explored an alternative method by treating our bacteria with{" "}
          <span>DTT</span> alone, and a combination of urea and DTT, prior to{" "}
          <span> boiling</span>. DTT, when introduced before boiling for Western
          blot, facilitates the cleavage of disulfide bonds. Furthermore, the
          inclusion of urea enhances <span>protein denaturation</span>. Through
          this approach, if the His-tag is sequestered within a protein complex,
          it could potentially be accessible.
        </p>

        <center>
          <details>
            <summary id="DTT_Urea">
              <mark>
                <b>DTT and urea experiment</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/dtt-and-urea-with-boiling.pdf"
              width="800"
              height="500"
              aria-label="DTT and urea experiment"
            ></object>
          </details>
                 
        </center>

        <h2 className=" mt-20" id="Surface_e">
          {" "}
          Checking that EC20 is on the surface{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          To check that EC20 is on the surface, we used two different approaches
          : a proteinase K assay, and immunofluorescence.
        </p>

        <h3 className="mb-5 mt-10" id="Proteinase K assay">
          Proteinase K assay
        </h3>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          In this experiment, we use a <span>protease</span> known as proteinase
          K, which belongs to the serine protease family. This experiment was
          also conducted by Bae, Weon, et al.
          <a href="#ref1">
            <sup>1</sup>{" "}
          </a>
          , whose work served as an inspiration for our study. We incubate our
          bacteria with this protease, and, over the course of this incubation,
          proteinase K effectively breaks down all the proteins that are located
          on the surface of the bacteria. By employing a Western blot analysis
          both before and after the incubation period, we could draw conclusions
          regarding the presence of our protein on the bacterial surface. If our
          target protein was indeed situated on the bacterial surface, we would
          observe a detectable signal in the Western blot before the proteinase
          K treatment, but this signal would disappear or significantly diminish
          after the treatment with proteinase K. The working principle of this
          experiment is schown on <a href="#Figure2">Figure 2</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure2"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/ec20-protk.png"
              alt="Schematic of the working system of Proteinase K on EC20"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 2:</u>{" "}
              </b>
              Schematic of the working system of Proteinase K on EC20
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Proteinase_K_protocol">
              <mark>
                <b>Proteinase K protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/proteinase-k-assay.pdf"
              width="800"
              height="500"
              aria-label="Proteinase K protocol"
            ></object>
          </details>
                 
        </center>

        <h3 className="mb-5 mt-10" id="Immunofluorescence">
          Immunofluorescence
        </h3>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Additionally, we conducted <span>immunofluorescence</span> experiments
          with our bacterial samples. We created our protocol based on Park,
          Seongjin, et al.
          <a href="#ref2">
            <sup>2</sup>{" "}
          </a>
          . If we observed a significantly higher level of fluorescence in the
          bacteria expressing EC20 compared to the negative control, this would
          indicate that EC20 was present on the surface of our bacteria. The
          schematic of this experiment can be seen on{" "}
          <a href="#Figure3">Figure 3</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure3"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/ec20-if.png"
              alt="Experiment_EC20"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 3:</u>{" "}
              </b>
              Schematic of the working system of Immunofluorescence on EC20
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="IF_protocol">
              <mark>
                <b>Immunofluorescence protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/immunofluorescence-for-surface-expression-verification.pdf"
              width="800"
              height="500"
              aria-label="Immunofluorescence protocol"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="cadmium_e">
          {" "}
          Checking that EC20 binds to cadmium{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Finally, we needed to check that our E20 construct could bind and
          capture <span>cadmium</span>. As cadmium is a highly toxic heavy
          metal, it became imperative to undertake a preliminary binding study,
          with a different metal. Consequently, we initiated our investigation
          by examining the binding affinity of our bacterial strain to zinc, a
          metal ion with a similar chemical structure to cadmium. Binding assays
          for both zinc and cadmium were conducted utilizing{" "}
          <span>colorimetric assay</span> kits. For zinc, we used the
          Spectroquant zinc test 1.14832.0001. For cadmium, we used the LCK308
          cuvette tests from HACH. <span>Absorbance</span> measurements
          facilitated by these kits enabled the quantification of the specific
          ion concentration within our samples. Testing whether our bacteria
          were able to bind to cadmium or zinc was achieved by calculating the
          difference in ion concentration before and after bacterial
          interaction. For the successful execution of this experiment, it was
          imperative to optimize several parameters. This included determining
          the optimal incubation duration, the appropriate bacterial quantity,
          and the precise concentration of cadmium/zinc to be added.
          Consequently, we conducted preliminary investigations to ascertain
          these optimal conditions before proceeding with the main experiment
          The schematic of the binding experiment can be seen on{" "}
          <a href="#Figure4">Figure 4</a>
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure4"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/cadmium.png"
              alt="Experiment_EC20"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 4:</u>{" "}
              </b>
              Schematic of the working system of the cadmium binding experiment
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Zinc_protocol">
              <mark>
                <b>Zinc binding assay</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/zinc-binding-experiment.pdf"
              width="800"
              height="500"
              aria-label="Zinc binding assay"
            ></object>
          </details>
                 
        </center>

        <center>
          <details>
            <summary id="Cadmium_protocol">
              <mark>
                <b>Cadmium binding assay</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/cadmium-binding-experiment.pdf"
              width="800"
              height="500"
              aria-label="Cadmium binding assay"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="References">
          {" "}
          References{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="ref1">
              {" "}
              Bae, Weon, et al. <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1002/1097-0290%2820001205%2970%3A5%3C518%3A%3AAID-BIT6%3E3.0.CO%3B2-5">
                Enhanced bioaccumulation of heavy metals by bacterial cells
                displaying synthetic phytochelatins.
              </a>{" "}
              <br />
              Biotechnology and bioengineering 70.5 (2000): 518-524{" "}
            </li>

            <li id="ref2">
              {" "}
              Park, Seongjin, et al. <br />{" "}
              <a href="https://pubs.acs.org/doi/full/10.1021/acs.biochem.9b00724">
                An improved method for bacterial immunofluorescence staining to
                eliminate antibody exclusion from the fixed nucleoid.
              </a>{" "}
              <br />
              Biochemistry 58.45 (2019): 4457-4465.{" "}
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
